import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Field, FieldsManager} from '../../../data/services/fields';
import Resources from '../../../data/services/resources';
import LocalStorage from '../../../util/localStorage';
import {createSecondResource, getSecondResource, updateSecondResource} from '../../../data/actions/secondResource';
import {uploadDocument} from '../../../data/actions/download';
import {
    CHECKLIST_HISTORY_TYPE_NOTE,
    CHECKLIST_HISTORY_TYPE_PHONE,
    CHECKLIST_HISTORY_TYPE_REMINDER,
    DEFAULT_DOCUMENTS_ACCEPTABLE_EXTENSIONS,
} from '../../../common/util/util-consts';
import {checkPerm, getLookup, getProp} from '../../../common/util/util-helpers';
import {CREATE_PERM, UPDATE_PERM} from '../../../util/util-constants';
import {timeZoneToUTC, toFrontDateNoTime} from '../../../common/util/util-dates';
import CheckList from '../../../common/components/checklist';
import ModalDefault from '../../../common/components/modal/modal-default';
import ModalSaveResource from '../../../common/components/modal/modal-save-resource';
import {fieldsToHtml} from '../../../common/util/util-fields';
import CalendarDaysIcon from '@heroicons/react/24/solid/CalendarDaysIcon';
import DocumentPreviewModal from '../../../common/components/tabs/documents-tab/document-preview-modal';

function ChecklistTab({id, ChecklistTemplateTypeID, translate, onChecklistUpdate}) {
    const secondResource = useSelector(state => state.secondResource);
    const download = useSelector(state => state.download);
    const dispatch = useDispatch();
    const [checklistSteps, setChecklistSteps] = useState([]);
    const [checklistUploadFiles, setChecklistUploadFiles] = useState([]);
    const [checklistUploadModalOpen, setChecklistUploadModalOpen] = useState(false);
    const [checklistUploadStepID, setChecklistUploadStepID] = useState(null);
    const [uploadingChecklistStepID, setUploadingChecklistStepID] = useState(null);
    const [isChecklistResetting, setIsChecklistResetting] = useState(false);
    const [previewDocument, setPreviewDocument] = useState(null);
    const [addNoteModal, setAddNoteModal] = useState({open: false, item: null, text: ''});
    const [logCallModal, setLogCallModal] = useState({open: false, item: null, text: ''});
    const [scheduleEventModal, setScheduleEventModal] = useState({
        open: false,
        item: null,
        selectedTarget: 'this_step'
    });

    const prevDownloadIsLoadingRef = useRef(download.isLoading);

    const getCheckListQuery = () => ({id});

    const fetchChecklistSteps = () => {
        dispatch(getSecondResource({
            user: LocalStorage.get('user'),
            resource: Resources.CustomersChecklist,
            query: getCheckListQuery(),
        }));
    };

    const handleChecklistVersionSelect = (version) => {
        dispatch(getSecondResource({
            user: LocalStorage.get('user'),
            resource: Resources.CustomersChecklist,
            query: Object.assign({id}, version ? {Version: version} : {}),
        }));
    };

    const handleChecklistSaveNote = (item, noteText) => {
        if (!checkPerm(Resources.CustomersChecklistStep, UPDATE_PERM)) return;
        dispatch(updateSecondResource({
            user: LocalStorage.get('user'),
            resource: Resources.CustomersChecklistStep,
            params: {ChecklistStepID: item.ChecklistStepID, Notes: noteText},
            piggyResource: Resources.CustomersChecklist,
            query: getCheckListQuery(),
        }));
    };

    const handleChecklistToggleSubStep = (item, idx, newCompleted, subStepID) => {
        if (!checkPerm(Resources.CustomersChecklistSubStep, UPDATE_PERM)) return;
        dispatch(updateSecondResource({
            user: LocalStorage.get('user'),
            resource: Resources.CustomersChecklistSubStep,
            params: {
                ChecklistStepID: item.ChecklistStepID,
                ChecklistSubStepID: subStepID,
                Completed: newCompleted ? 1 : 0,
            },
            piggyResource: Resources.CustomersChecklist,
            query: getCheckListQuery(),
        }));
        onChecklistUpdate?.();
    };

    const handleChecklistStepAction = (item, stepID, statusID) => {
        if (!checkPerm(Resources.CustomersChecklistStep, UPDATE_PERM)) return;
        dispatch(updateSecondResource({
            user: LocalStorage.get('user'),
            resource: Resources.CustomersChecklistStep,
            params: {ChecklistStepID: stepID, StatusID: statusID},
            piggyResource: Resources.CustomersChecklist,
            query: getCheckListQuery(),
        }));
        onChecklistUpdate?.();
    };

    const handleChecklistFilesDrop = (files, item, preselectedDocumentTypeID = null) => {
        if (!files.length) return;

        const enriched = files.map((file) => Object.assign(file, {
            preview: URL.createObjectURL(file),
            errorMessage: '',
            fields: {
                DocumentTypeID: new Field('DocumentTypeID', preselectedDocumentTypeID ?? '', ['empty'], false, 'select'),
                ExpiryDate: new Field('ExpiryDate', '', [], false, 'date'),
                Description: new Field('Description', '', [], false, 'textarea'),
            },
        }));

        setChecklistUploadFiles(enriched);
        setChecklistUploadModalOpen(true);
        setChecklistUploadStepID(item?.ChecklistStepID ?? null);
    };

    const handleCancelChecklistUpload = () => {
        setChecklistUploadFiles([]);
        setChecklistUploadModalOpen(false);
        setChecklistUploadStepID(null);
    };

    const handleChecklistUploadInputChange = (name, value, i) => {
        setChecklistUploadFiles(prev => {
            const updated = [...prev];
            updated[i].fields = FieldsManager.updateField(updated[i].fields, name, value);
            return updated;
        });
    };

    const handleSubmitChecklistUpload = () => {
        const validated = checklistUploadFiles.map((file) =>
            Object.assign(file, {fields: FieldsManager.validateFields(file.fields)})
        );

        const allValid = validated.reduce(
            (memo, file) => memo && FieldsManager.checkFieldsForErrors(file.fields),
            true
        );

        setChecklistUploadFiles(validated);

        if (!allValid) return;

        const files = validated.map((file) =>
            Object.assign(file, {
                params: Object.assign({}, FieldsManager.getFieldKeyValues(file.fields), {Version: 0}),
            })
        );

        const uploadingStepID = checklistUploadStepID;

        dispatch(uploadDocument({
            user: LocalStorage.get('user'),
            files,
            id,
            query: getCheckListQuery(),
            params: {ChecklistStepID: uploadingStepID},
            resource: Resources.CustomersChecklistDocuments,
            errorMessage: true,
            successMessage: `Attachment${files.length > 1 ? 's' : ''} ${files.length > 1 ? 'have' : 'has'} been uploaded successfully.`,
        }));

        handleCancelChecklistUpload();
        setUploadingChecklistStepID(uploadingStepID);
    };

    const handleArchiveAndUseLatestTemplate = () => {
        setIsChecklistResetting(true);

        dispatch(createSecondResource({
            user: LocalStorage.get('user'),
            resource: Resources.CustomersChecklist,
            params: {
                id,
                ChecklistTemplateTypeID: ChecklistTemplateTypeID,
            },
            piggyResource: Resources.CustomersChecklist,
            query: getCheckListQuery(),
            errorMessage: true,
            successMessage: translate('message.checklist_archived_and_updated'),
        }));
        onChecklistUpdate?.();
    };

    const handleChecklistAddNote = () => {
        const {item, text} = addNoteModal;

        dispatch(createSecondResource({
            user: LocalStorage.get('user'),
            resource: Resources.CustomersChecklistStepHistory,
            params: {ChecklistStepID: item.ChecklistStepID, Text: text, TypeID: CHECKLIST_HISTORY_TYPE_NOTE},
            piggyResource: Resources.CustomersChecklist,
            query: getCheckListQuery(),
        }));

        setAddNoteModal({open: false, item: null, text: ''});
    };

    const handleChecklistLogCall = () => {
        const {item, text} = logCallModal;

        dispatch(createSecondResource({
            user: LocalStorage.get('user'),
            resource: Resources.CustomersChecklistStepHistory,
            params: {ChecklistStepID: item.ChecklistStepID, Text: text, TypeID: CHECKLIST_HISTORY_TYPE_PHONE},
            piggyResource: Resources.CustomersChecklist,
            query: getCheckListQuery(),
        }));

        setLogCallModal({open: false, item: null, text: ''});
    };

    const getScheduleEventFields = () => ({
        IsAllDay: new Field('IsAllDay', '', [], false, 'checkbox', {
            addContainerClass: 'col-span-full',
            label: 'IsAllDayReminder',
        }),
        ReminderDate: new Field('ReminderDate', '', ['empty'], false, 'date', {}, {isClearable: true}),
        ReminderTime: new Field('ReminderTime', '', [], false, 'time-custom-new', {withDateField: 'ReminderDate'}, {isClearable: true}),
        Notes: new Field('Notes', '', [], false, 'textarea', {
            label: 'ReminderNotes',
            addContainerClass: 'col-span-full',
        }),
    });

    const handleScheduleEventInputChange = (fields, name, value) => {
        const updated = FieldsManager.updateField(fields, name, value);

        if (name === 'IsAllDay') {
            updated.ReminderDate.metadata.addContainerClass = value ? 'col-span-full' : undefined;
            updated.ReminderTime.type = value ? 'hidden' : 'time-custom-new';
        }

        return updated;
    };

    const handleScheduleEventSubmit = (params) => {
        if (!params) return;

        const {item, selectedTarget} = scheduleEventModal;

        const reminderDate = timeZoneToUTC(
            params.IsAllDay
                ? params.ReminderDate
                : params.ReminderDate.split(' ')[0] + ' ' + (params.ReminderTime || '00:00:00')
        );

        dispatch(createSecondResource({
            user: LocalStorage.get('user'),
            resource: Resources.CustomersReminder,
            params: {
                CustomerID: id,
                ChecklistStepID: item.ChecklistStepID,
                ReminderDate: reminderDate,
                IsAllDay: params.IsAllDay,
                Notes: params.Notes,
                TypeID: CHECKLIST_HISTORY_TYPE_REMINDER,
                ...(selectedTarget !== 'this_step' ? {ChecklistSubStepID: selectedTarget} : {}),
            },
            piggyResource: Resources.CustomersChecklist,
            query: getCheckListQuery(),
        }));

        setScheduleEventModal({open: false, item: null, selectedTarget: 'this_step'});
    };

    const handleReturnExpiryDate = (item) => {
        const ExpiryDate = item.ExpiryDate;
        const today = new Date().toISOString().slice(0, 19).replace('T', ' ');
        const xDaysBefore = new Date(new Date(ExpiryDate) - 10 * 86400000).toISOString().slice(0, 19).replace('T', ' ');
        let styles = 'text-tm-gray-700 py-0.5 h-6';
        if (ExpiryDate) {
            if (xDaysBefore < today && today < ExpiryDate) {
                styles = 'btn-warning text-inverse';
            }
            if (today > ExpiryDate) {
                styles = 'btn-danger';
            }
        }
        return (
            <button className={`btn btn-xs active ${styles}`}>
                {toFrontDateNoTime(ExpiryDate)}
            </button>
        );
    };

    useEffect(() => {
        fetchChecklistSteps();
    }, [id]);

    useEffect(() => {
        const checklistData = getProp(secondResource, 'data', null);
        if (checklistData !== null) {
            setChecklistSteps(checklistData);
        }
        if (!secondResource.isLoading && isChecklistResetting) {
            setIsChecklistResetting(false);
        }
    }, [secondResource]);

    useEffect(() => {
        const wasLoading = prevDownloadIsLoadingRef.current;
        prevDownloadIsLoadingRef.current = download.isLoading;

        if (wasLoading && !download.isLoading && uploadingChecklistStepID !== null) {
            setUploadingChecklistStepID(null);
            if (!download.error) {
                fetchChecklistSteps();
            }
        }
    }, [download]);

    const canCreateHistory = checkPerm(Resources.CustomersChecklistStepHistory, CREATE_PERM);
    const canCreateDocument = checkPerm(Resources.CustomersChecklistDocuments, CREATE_PERM);
    const canCreateReminder = checkPerm(Resources.CustomersReminder, CREATE_PERM);
    const canCreateChecklist = checkPerm(Resources.CustomersChecklist, CREATE_PERM);
    const canUpdateSubStep = checkPerm(Resources.CustomersChecklistSubStep, UPDATE_PERM);

    return (
        <>
            <CheckList
                translate={translate}
                title="Customer onboarding"
                isLoading={secondResource.isLoading}
                uploadingStepID={uploadingChecklistStepID}
                query={getCheckListQuery()}
                actions={[
                    {
                        key: "add_note",
                        label: "Add Note",
                        hasPerm: canCreateHistory,
                        icon: (
                            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
                                 className="size-4 shrink-0 text-tm-gray-400">
                                <path
                                    d="M2.695 14.763l-1.262 3.154a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.885L17.5 5.5a2.121 2.121 0 0 0-3-3L3.58 13.42a4 4 0 0 0-.885 1.343Z"/>
                            </svg>
                        ),
                        onClick: (item) => setAddNoteModal({open: true, item, text: ''}),
                    },
                    {
                        key: "log_call",
                        label: "Log Call",
                        hasPerm: canCreateHistory,
                        icon: (
                            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
                                 className="size-4 shrink-0 text-tm-gray-400">
                                <path fillRule="evenodd" clipRule="evenodd"
                                      d="M2 3.5A1.5 1.5 0 0 1 3.5 2h1.148a1.5 1.5 0 0 1 1.465 1.175l.716 3.223a1.5 1.5 0 0 1-1.052 1.767l-.933.267c-.41.117-.643.555-.48.95a11.542 11.542 0 0 0 6.254 6.254c.395.163.833-.07.95-.48l.267-.933a1.5 1.5 0 0 1 1.767-1.052l3.223.716A1.5 1.5 0 0 1 18 15.352V16.5a1.5 1.5 0 0 1-1.5 1.5H15c-1.149 0-2.263-.15-3.326-.43A13.022 13.022 0 0 1 2.43 8.326 13.019 13.019 0 0 1 2 5V3.5Z"/>
                            </svg>
                        ),
                        onClick: (item) => setLogCallModal({open: true, item, text: ''}),
                    },
                    {
                        key: "add_document",
                        label: "Add Document",
                        hasPerm: canCreateDocument,
                        icon: (
                            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
                                 className="size-4 shrink-0 text-tm-gray-400">
                                <path
                                    d="M3 3.5A1.5 1.5 0 0 1 4.5 2h6.879a1.5 1.5 0 0 1 1.06.44l4.122 4.12A1.5 1.5 0 0 1 17 7.622V16.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 16.5v-13Zm10.5 4a.5.5 0 0 1-.5-.5v-2.88l3 2.99-.5.39H13.5ZM8.75 8a.75.75 0 0 0 0 1.5h2.5a.75.75 0 0 0 0-1.5h-2.5Zm0 3a.75.75 0 0 0 0 1.5h4.5a.75.75 0 0 0 0-1.5h-4.5Zm0 3a.75.75 0 0 0 0 1.5h4.5a.75.75 0 0 0 0-1.5h-4.5Z"/>
                            </svg>
                        ),
                        openFileDialog: true,
                        onClick: () => {
                        },
                    },
                    {
                        key: "schedule_event",
                        label: "Set Reminder",
                        hasPerm: canCreateReminder,
                        icon: (
                            <CalendarDaysIcon className="size-4 shrink-0 text-tm-gray-400"/>
                        ),
                        onClick: (item) => setScheduleEventModal({open: true, item, selectedTarget: 'this_step'}),
                    },
                ]}
                items={checklistSteps?.Steps ?? []}
                RequiredDocuments={checklistSteps?.RequiredDocuments ?? []}
                Documents={checklistSteps?.Documents ?? []}
                Versions={checklistSteps?.Versions ?? []}
                onVersionSelect={handleChecklistVersionSelect}
                onFilesDrop={handleChecklistFilesDrop}
                accept={DEFAULT_DOCUMENTS_ACCEPTABLE_EXTENSIONS}
                onPreviewDocument={(doc) => setPreviewDocument(doc)}
                onSaveNote={(item, note) => handleChecklistSaveNote(item, note)}
                onToggleSubStep={(item, idx, newCompleted, subStepID) => handleChecklistToggleSubStep(item, idx, newCompleted, subStepID)}
                onStepAction={(item, stepID, statusID) => handleChecklistStepAction(item, stepID, statusID)}
                onArchiveAndUseLatestTemplate={handleArchiveAndUseLatestTemplate}
                canCreateChecklist={canCreateChecklist}
                canToggleSubStep={canUpdateSubStep}
                notFound={getProp(secondResource, 'errorMessage', null) === "CHECKLIST_NOT_FOUND"}
                isResetting={isChecklistResetting}
            />

            <ModalDefault
                show={!!addNoteModal.open}
                title={translate('modal_heading.add_note')}
                widthClass={'max-w-lg'}
                closeButtonLabel={translate('btn.cancel')}
                onClose={() => setAddNoteModal({open: false, item: null, text: ''})}
                buttonLabel={translate('btn.save')}
                onButtonClick={handleChecklistAddNote}
                translate={translate}
            >
                <div className="p-6">
                    <textarea
                        className="form-control"
                        placeholder={translate('text.add_note_placeholder')}
                        rows={4}
                        value={addNoteModal.text}
                        onChange={e => setAddNoteModal(prev => ({...prev, text: e.target.value}))}
                        autoFocus
                    />
                </div>
            </ModalDefault>

            <ModalDefault
                show={!!logCallModal.open}
                title={translate('modal_heading.log_call')}
                widthClass={'max-w-lg'}
                closeButtonLabel={translate('btn.cancel')}
                onClose={() => setLogCallModal({open: false, item: null, text: ''})}
                buttonLabel={translate('btn.save')}
                onButtonClick={handleChecklistLogCall}
                translate={translate}
            >
                <div className="p-6">
                    <textarea
                        className="form-control"
                        placeholder={translate('text.add_note_placeholder')}
                        rows={4}
                        value={logCallModal.text}
                        onChange={e => setLogCallModal(prev => ({...prev, text: e.target.value}))}
                        autoFocus
                    />
                </div>
            </ModalDefault>

            <ModalDefault
                show={checklistUploadModalOpen}
                widthClass="max-w-3xl"
                title={translate('modal_heading.upload_confirm')}
                limitHeight={true}
                closeButtonLabel={translate('btn.cancel')}
                onClose={handleCancelChecklistUpload}
                buttonLabel={translate('Upload')}
                onButtonClick={handleSubmitChecklistUpload}
                translate={translate}
            >
                <div className="px-5 pt-5 pb-40">
                    {checklistUploadFiles.map((it, i) => {
                        const itemToHtml = fieldsToHtml(
                            Object.values(Object.assign({}, it.fields)),
                            translate,
                            (name, value) => handleChecklistUploadInputChange(name, value, i),
                            {DocumentTypeID: getLookup('DocumentType')}
                        );

                        return (
                            <div key={i}>
                                {!!i && (
                                    <div className="border-b border-tm-gray-300 mb-4 p-4 -ml-4 -mr-4"/>
                                )}

                                <div className="flex items-center mb-4">
                                    <label className="text-sm font-semibold text-tm-gray-900 break-all">
                                        {translate('text.desc_for_file')}: {it.name}
                                    </label>
                                </div>

                                <div className="grid gap-4">
                                    {itemToHtml}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </ModalDefault>

            <ModalSaveResource
                title={translate('modal_heading.set_reminder')}
                show={scheduleEventModal.open}
                widthClass="max-w-md"
                gridColsClass="grid-cols-2"
                fields={getScheduleEventFields()}
                handleInputChange={handleScheduleEventInputChange}
                buttonLabel={translate('btn.save')}
                closeButtonLabel={translate('btn.cancel')}
                translate={translate}
                onClose={() => setScheduleEventModal({open: false, item: null, selectedTarget: 'this_step'})}
                onSubmit={handleScheduleEventSubmit}
                htmlAfter={
                    (scheduleEventModal.item?.SubSteps?.length > 0) && (
                        <div className="px-6 pb-6">
                            <p className="text-sm font-semibold text-tm-gray-900 mb-2">
                                {translate('text.set_reminder_for')}
                            </p>

                            <div className="flex flex-col gap-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="schedule_event_target"
                                        value="this_step"
                                        checked={scheduleEventModal.selectedTarget === 'this_step'}
                                        onChange={() => setScheduleEventModal(prev => ({
                                            ...prev,
                                            selectedTarget: 'this_step'
                                        }))}
                                        className="accent-primary"
                                    />

                                    <span className="text-sm text-tm-gray-700">{translate('text.this_step')}</span>
                                </label>

                                {scheduleEventModal.item.SubSteps.map(subStep => (
                                    <label key={subStep.ChecklistSubStepID}
                                           className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="schedule_event_target"
                                            value={subStep.ChecklistSubStepID}
                                            checked={scheduleEventModal.selectedTarget === subStep.ChecklistSubStepID}
                                            onChange={() => setScheduleEventModal(prev => ({
                                                ...prev,
                                                selectedTarget: subStep.ChecklistSubStepID
                                            }))}
                                            className="accent-primary"
                                        />

                                        <span className="text-sm text-tm-gray-700">{subStep.Label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )
                }
            />
            <DocumentPreviewModal
                show={!!previewDocument}
                translate={translate}
                item={previewDocument}
                onClose={() => setPreviewDocument(null)}
                onButtonClick={() => setPreviewDocument(null)}
                buttonLabel={translate('btn.close')}
                closeButtonLabel={translate('btn.close')}
                id={id}
                primaryKey="DocumentID"
                resourceName={Resources.CustomersChecklistDocuments}
                isLoading={download.isLoading}
                onReturnExpiryDate={handleReturnExpiryDate}
            />
        </>
    );
}

export default ChecklistTab;
